import { type FC, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { trackPromoImpressions, trackPromoClick } from '../helpers/sbSharedFeaturedTracking.ts';
import { useCurrency } from '../../shared/hooks/useCurrency.ts';

// components
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'dibs-react-intl';
import { SbSharedL1FeaturedTile } from '../SbSharedL1FeaturedTile/SbSharedL1FeaturedTile.tsx';

// types
import { type SbSharedFeaturedDeal_itemSearch$key } from './__generated__/SbSharedFeaturedDeal_itemSearch.graphql.ts';
import { type SbSharedFeaturedDeal_l1Module$key } from './__generated__/SbSharedFeaturedDeal_l1Module.graphql.ts';

type Vertical = 'furniture' | 'art' | 'jewelry' | 'fashion';

type Props = {
    itemSearch: SbSharedFeaturedDeal_itemSearch$key;
    l1Module: SbSharedFeaturedDeal_l1Module$key;
    position?: number;
};

const verticalMessages = defineMessages<Vertical>({
    furniture: {
        id: 'SbSharedFeaturedDeal.price.furniture',
        defaultMessage: 'Furniture under {price}',
    },
    art: {
        id: 'SbSharedFeaturedDeal.price.art',
        defaultMessage: 'Art under {price}',
    },
    jewelry: {
        id: 'SbSharedFeaturedDeal.price.jewelry',
        defaultMessage: 'Jewelry under {price}',
    },
    fashion: {
        id: 'SbSharedFeaturedDeal.price.fashion',
        defaultMessage: 'Fashion under {price}',
    },
});

const itemSearchFragment = graphql`
    fragment SbSharedFeaturedDeal_itemSearch on ItemSearchQueryConnection {
        featuredDeal {
            imageUrl
            vertical
            convertedAmountList {
                amount
                currency
                linkData {
                    ...SeoLink_linkData
                }
            }
        }
    }
`;
const l1ModuleFragment = graphql`
    fragment SbSharedFeaturedDeal_l1Module on L1Module {
        firstTile {
            mainImage
        }
    }
`;

const CREATIVE_LABEL = 'price bucket';

const heading = <FormattedMessage id="SbSharedFeaturedDeal.heading" defaultMessage="Shop Now" />;

export const SbSharedFeaturedDeal: FC<Props> = ({
    itemSearch: itemSearchRefRef,
    l1Module: l1ModuleRef,
    position,
}) => {
    const intl = useIntl();
    const { currency } = useCurrency();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRefRef);
    const l1Module = useFragment(l1ModuleFragment, l1ModuleRef);
    const { featuredDeal } = itemSearch || {};
    const { imageUrl, convertedAmountList = null, vertical } = featuredDeal || {};
    const { firstTile } = l1Module || {};
    const { linkData, amount } =
        convertedAmountList?.find(convertedAmount => convertedAmount?.currency === currency) || {};

    useEffect(() => {
        if (amount && vertical) {
            trackPromoImpressions({
                promotions: [CREATIVE_LABEL],
                position,
            });
        }
    }, [amount, vertical, position]);

    if (!amount || !vertical || !linkData) {
        return null;
    }

    const subHeading = intl.formatMessage(verticalMessages[vertical as Vertical], {
        price: (
            <FormattedNumber
                value={amount}
                style="currency"
                currency={currency}
                minimumFractionDigits={0}
                key="SbSharedFeaturedDeal-subHeading-price"
            />
        ),
    });

    return (
        <SbSharedL1FeaturedTile
            mainImage={firstTile?.mainImage || imageUrl}
            heading={heading}
            subHeading={subHeading}
        >
            {({ className, children }) => (
                <SeoLink
                    className={className}
                    linkData={linkData}
                    onClick={() => trackPromoClick({ position, promotion: CREATIVE_LABEL })}
                >
                    {children}
                </SeoLink>
            )}
        </SbSharedL1FeaturedTile>
    );
};
